/**
 * PartnerTiersPage — Pricing v3 marketing surface for PooPartner tiers.
 *
 * ROUTE: `/partner/tiers` (public, unauth).
 *
 * Since Feb 2026 the tier-cards + Level Up ladder + payout table live
 * in the shared `<PartnerTiersPanel>` component so the `/pricing`
 * "For PooPartners" tab and this page render the SAME surface. This
 * page contributes only the outer chrome (public shell + heading +
 * subhead + secondary CTA) and owns the CTA behavior (auth check +
 * INTENT-only POST + toast).
 *
 * Canonical visual spec: `/app/memory/poopartner_tiers_final.html`.
 * Audit trail: `/app/memory/TIER_PAGE_SPEC.md`.
 *
 * CTAs (unchanged from the pre-extract shape — the panel invokes
 * `onTierCta(tierId)` and this component dispatches):
 *   - Standard "Start free" → /partner/apply
 *   - Preferred "Upgrade" → POST /api/partner/tier/select {tier_id:"preferred"}
 *     (authed provider) OR /partner/apply?intent=preferred (unauth)
 *   - Elite "Get Elite" → POST /api/partner/tier/select {tier_id:"elite"}
 *     (authed provider) OR /partner/apply?intent=elite (unauth)
 *   - Enterprise "Talk to us" → /contact?source=enterprise
 *
 * The POST is INTENT-only — no billing dispatch, no Stripe charge.
 * A "billing coming soon" toast surfaces on success so partners
 * know their selection has been captured.
 */
import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { trackEvent } from "@/lib/analytics";
import { useAuth } from "@/context/AuthContext";
import PublicPageShell from "@/components/PublicPageShell";
import PartnerTiersPanel from "@/components/PartnerTiersPanel";
import WinnerSlotCard from "@/components/WinnerSlotCard";

const CHAR = "#374751";
const GREY = "#EEF1F3";
const MUTED = "#6B7A85";

export default function PartnerTiersPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [busyTierId, setBusyTierId] = useState(null);

  const isAuthedPartner = !!user && !!user.is_provider;

  async function handleSelectableTier(tierId) {
    // Preferred / Elite go through POST /api/partner/tier/select when
    // the user is an authed partner; otherwise route to the pre-auth
    // funnel with an ?intent= query for post-signup selection.
    // Prod Hotfix Batch 1 P0-2 — target flipped from `/partner/apply`
    // (retired) to `/signup?role=provider`. Intent parameter preserved
    // for the eventual post-signup tier pre-select handoff.
    if (!isAuthedPartner) {
      navigate(`/signup?role=provider&intent=${tierId}`);
      return;
    }
    setBusyTierId(tierId);
    try {
      await api.post("/partner/tier/select", { tier_id: tierId });
      // Commit B — §4.4. Tier-selection is the "save" action per
      // design doc — this event feeds the interest-vs-conversion
      // ratio for each tier.
      trackEvent("tier_interest.save", { tier_id: tierId });
      toast.success(
        "Selection saved. Billing dispatch is coming soon — no charge yet.",
        { duration: 4200 },
      );
    } catch (err) {
      const msg = formatApiErrorDetail(err?.response?.data?.detail) ||
                  "Could not save your tier selection. Please try again.";
      toast.error(msg);
    } finally {
      setBusyTierId(null);
    }
  }

  function handleCta(tierId) {
    // Standard's "Start free" routes to the pre-auth signup funnel.
    // Prod Hotfix Batch 1 P0-2 — target flipped from `/partner/apply`
    // (retired) to `/signup?role=provider`.
    if (tierId === "standard") {
      navigate("/signup?role=provider");
      return;
    }
    if (tierId === "enterprise") {
      navigate("/contact?source=enterprise");
      return;
    }
    handleSelectableTier(tierId);
  }

  return (
    <PublicPageShell mainClass="">
      <div
        style={{
          background: GREY,
          color: CHAR,
          padding: "40px 24px",
          minHeight: "100vh",
        }}
        data-testid="partner-tiers-page"
      >
        <div style={{ maxWidth: 1280, margin: "0 auto" }}>
          <h1
            style={{
              fontSize: 30,
              fontWeight: 800,
              letterSpacing: "-.5px",
            }}
            data-testid="tiers-h1"
          >
            PooPartner Tiers — the ladder at a glance
          </h1>
          <p
            style={{
              color: MUTED,
              margin: "6px 0 34px",
              fontSize: 15,
            }}
            data-testid="tiers-sub"
          >
            Segmented by business scale. Volume thresholds auto-promote (grace period + it saves them money). White-label skipped for now — Elite gains branded exports (their logo on PDFs/reports) as a future perk instead.
          </p>

          <PartnerTiersPanel onTierCta={handleCta} busyTierId={busyTierId} />

          <div style={{ marginTop: 36, textAlign: "center" }}>
            <Link
              to="/signup?role=provider"
              data-testid="tiers-secondary-cta"
              style={{
                display: "inline-block",
                padding: "12px 22px",
                background: CHAR,
                color: "#fff",
                borderRadius: 999,
                textDecoration: "none",
                fontWeight: 700,
                fontSize: 14,
              }}
            >
              Not sure yet? Start free
            </Link>
          </div>
          <WinnerSlotCard winnerRank={8} />
        </div>
      </div>
    </PublicPageShell>
  );
}
